import { useState, useEffect } from 'react';
import { Card, Button, TextField, Modal, RadioButton, Text, InlineStack, BlockStack, Box } from '@shopify/polaris';
import { CardHeading } from '../../../shared/components/cards/CardHeading.jsx';

export default function QuickStockAdjust({ inventories, setInventories, product, pickingplaces, refetchInventory, showOn = 'both' }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [customAmount, setCustomAmount] = useState('');
  const [isPositive, setIsPositive] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInventoryIndex, setSelectedInventoryIndex] = useState(0);
  const [pendingAdjustment, setPendingAdjustment] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Control visibility based on showOn prop and viewport
  const shouldShow = showOn === 'both' || (showOn === 'mobile' && isMobile) || (showOn === 'desktop' && !isMobile);

  // Don't render if no inventories or wrong viewport
  if (!shouldShow || !inventories || inventories.length === 0) {
    return null;
  }

  const getPickingplaceName = (stockplaatsId) => {
    const place = pickingplaces?.find((p) => p.counter === stockplaatsId);
    return place?.pickingplace || `Location ${stockplaatsId}`;
  };

  const updateInventoryAPI = async (inventory, adjustment) => {
    const newQuantity = (parseInt(inventory.aantal) || 0) + adjustment;
    if (newQuantity < 0) {
      alert('Cannot reduce stock below 0');
      return false;
    }

    const payload = {
      Productid: inventory.Productid,
      Stockplaats: inventory.Stockplaats,
      aantal: newQuantity,
      Rack: inventory.Rack,
      Legplank: inventory.Legplank,
      isInventoryCount: true
    };

    try {
      const response = await fetch(`/api/inventory/${inventory.counter}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update inventory');
      }

      // Refresh inventory data
      if (refetchInventory) {
        await refetchInventory();
      } else {
        // Update local state as fallback
        setInventories((old) => old.map((inv) => (inv.counter === inventory.counter ? { ...inv, aantal: newQuantity } : inv)));
      }
      return true;
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('Failed to update stock. Please try again.');
      return false;
    }
  };

  const handleQuickAdjust = async (adjustment) => {
    if (inventories.length === 1) {
      // Single location - adjust directly
      setIsSubmitting(true);
      await updateInventoryAPI(inventories[0], adjustment);
      setIsSubmitting(false);
    } else {
      // Multiple locations - show modal
      setPendingAdjustment(adjustment);
      setSelectedInventoryIndex(0);
      setModalOpen(true);
    }
  };

  const handleCustomAdjust = async () => {
    const amount = parseInt(customAmount);
    if (isNaN(amount) || amount === 0) {
      return;
    }
    const adjustment = isPositive ? amount : -amount;
    await handleQuickAdjust(adjustment);
    setCustomAmount('');
  };

  const handleModalConfirm = async () => {
    setIsSubmitting(true);
    const selectedInventory = inventories[selectedInventoryIndex];
    await updateInventoryAPI(selectedInventory, pendingAdjustment);
    setModalOpen(false);
    setIsSubmitting(false);
  };

  const quickButtons = [
    { label: '-10', value: -10 },
    { label: '-5', value: -5 },
    { label: '-3', value: -3 },
    { label: '-2', value: -2 },
    { label: '-1', value: -1 }
  ];

  const quickButtonsPositive = [
    { label: '+1', value: 1 },
    { label: '+2', value: 2 },
    { label: '+3', value: 3 },
    { label: '+5', value: 5 },
    { label: '+10', value: 10 }
  ];

  // Get total stock and primary location
  const totalStock = inventories.reduce((sum, inv) => sum + (parseInt(inv.aantal) || 0), 0);
  const primaryInventory = inventories[0];
  const primaryLocationName = getPickingplaceName(primaryInventory?.Stockplaats);

  return (
    <>
      <Card>
        <CardHeading title="Quick Stock Adjust" />
        <BlockStack gap="400">
          <Box padding="200" background="bg-surface-secondary" borderRadius="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="span" fontWeight="semibold">
                Current Stock:
              </Text>
              <Text as="span" fontWeight="bold" variant="headingLg">
                {inventories.length === 1 ? (
                  <>
                    {primaryInventory?.aantal || 0}
                    <Text as="span" tone="subdued" variant="bodySm">
                      {' '}
                      ({primaryLocationName})
                    </Text>
                  </>
                ) : (
                  <>
                    {totalStock}
                    <Text as="span" tone="subdued" variant="bodySm">
                      {' '}
                      ({inventories.length} locations)
                    </Text>
                  </>
                )}
              </Text>
            </InlineStack>
          </Box>

          {/* Negative adjustment buttons */}
          <InlineStack gap="200" wrap={true}>
            {quickButtons.map((btn) => (
              <Button key={btn.value} onClick={() => handleQuickAdjust(btn.value)} disabled={isSubmitting} tone="critical">
                {btn.label}
              </Button>
            ))}
          </InlineStack>

          {/* Positive adjustment buttons */}
          <InlineStack gap="200" wrap={true}>
            {quickButtonsPositive.map((btn) => (
              <Button key={btn.value} onClick={() => handleQuickAdjust(btn.value)} disabled={isSubmitting} tone="success">
                {btn.label}
              </Button>
            ))}
          </InlineStack>

          {/* Custom amount input */}
          <InlineStack gap="200" blockAlign="end" wrap={false}>
            <div style={{ flex: 1 }}>
              <TextField label="Custom amount" type="number" value={customAmount} onChange={setCustomAmount} disabled={isSubmitting} min="1" autoComplete="off" />
            </div>
            <Button onClick={() => setIsPositive(!isPositive)} disabled={isSubmitting}>
              {isPositive ? '+' : '-'}
            </Button>
            <Button onClick={handleCustomAdjust} disabled={isSubmitting || !customAmount} variant="primary">
              Apply
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>

      {/* Stock Location Selection Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Select Stock Location"
        primaryAction={{
          content: `Adjust: ${pendingAdjustment > 0 ? '+' : ''}${pendingAdjustment}`,
          onAction: handleModalConfirm,
          loading: isSubmitting
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalOpen(false),
            disabled: isSubmitting
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              Select which stock location to adjust:
            </Text>
            {inventories.map((inv, index) => (
              <div key={index} onClick={() => setSelectedInventoryIndex(index)} style={{ cursor: 'pointer' }}>
                <Box
                  padding="300"
                  background={selectedInventoryIndex === index ? 'bg-surface-selected' : 'bg-surface-secondary'}
                  borderRadius="200"
                  borderWidth="025"
                  borderColor={selectedInventoryIndex === index ? 'border-emphasis' : 'border'}
                >
                  <InlineStack align="space-between" blockAlign="center">
                    <RadioButton
                      label={
                        <BlockStack gap="100">
                          <Text as="span" fontWeight="semibold">
                            {getPickingplaceName(inv.Stockplaats)} - Qty: {inv.aantal || 0}
                          </Text>
                          <Text as="span" tone="subdued" variant="bodySm">
                            Rack: {inv.Rack || '-'} | Shelf: {inv.Legplank || '-'}
                          </Text>
                        </BlockStack>
                      }
                      checked={selectedInventoryIndex === index}
                      onChange={() => setSelectedInventoryIndex(index)}
                    />
                  </InlineStack>
                </Box>
              </div>
            ))}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
